import React, { useEffect, useMemo, useState } from 'react'
import { FaSave } from 'react-icons/fa'
import { FaUpload } from 'react-icons/fa6'
import CustomNoData from '../../components/CustomNoData'
import EventSelectionCheckbox from '../../components/EventSelectionCheckbox'
import PaginationV2 from '../../components/pagination_v2'

const valueToInput = (value) => {
  if (value === null || value === undefined) {
    return ''
  }
  return String(value)
}

const formatKickoff = (value) => {
  if (!value) return 'TBD'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return `${date.toLocaleDateString()} | ${date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })}`
}

const inputClassName =
  'h-10 w-[75%] rounded-xl border border-customAccent/20 bg-white px-3 text-sm font-semibold text-customInk shadow-sm transition placeholder:text-customMuted focus:border-customAccent focus:outline-none focus:ring-2 focus:ring-customAccent/20 disabled:cursor-not-allowed disabled:bg-customSlate/60 disabled:text-customMuted dark:border-customSurfaceMuted dark:bg-custom_dark_secondary dark:text-customHeading'

const hasOddsValue = (value) => {
  const trimmed = value === null || value === undefined ? '' : String(value).trim()
  return Boolean(trimmed) && trimmed.toUpperCase() !== 'N/A'
}

const EmergencyOddsInputs = ({ values, onChange }) => {
  const hasHomeOdds = hasOddsValue(values.homeOdds)
  const hasAwayOdds = hasOddsValue(values.awayOdds)

  return (
  <div className="flex w-full gap-2">
    <label className="grid min-w-0 flex-1 gap-1 text-[10px] font-bold uppercase tracking-wide text-customMuted">
      Home odds
      <input
        type="text"
        value={values.homeOdds}
        onChange={(event) => onChange('homeOdds', event.target.value)}
        disabled={hasAwayOdds && !hasHomeOdds}
        className={inputClassName}
        placeholder="N/A"
      />
    </label>
    <label className="grid min-w-0 flex-1 gap-1 text-[10px] font-bold uppercase tracking-wide text-customMuted">
      Away odds
      <input
        type="text"
        value={values.awayOdds}
        onChange={(event) => onChange('awayOdds', event.target.value)}
        disabled={hasHomeOdds && !hasAwayOdds}
        className={inputClassName}
        placeholder="N/A"
      />
    </label>
  </div>
  )
}

const EmergencyGoalInput = ({ values, onChange }) => (
  <label className="grid min-w-[160px] gap-1 text-[10px] font-bold uppercase tracking-wide text-customMuted">
    Goal odds
    <input
      type="text"
      value={values.goalOdds}
      onChange={(event) => onChange('goalOdds', event.target.value)}
      className={inputClassName}
      placeholder="Goal"
    />
  </label>
)

const EmergencyV0 = ({
  events = [],
  allEvents = events,
  selectionMap = {},
  onToggleSelection,
  onSave,
  onSaveMultiple,
  saving = false,
  pagination = null,
  userDetails,
  onManualSave,
  rowSaveLoading = {},
}) => {
  const [draftValues, setDraftValues] = useState({})

  useEffect(() => {
    setDraftValues((previous) => {
      const next = { ...previous }
      allEvents.forEach((event) => {
        const eventKey = String(event.id)
        next[eventKey] = {
          homeOdds: previous[eventKey]?.homeOdds ?? valueToInput(event.homeTeamId === event.overTeamId ? event.burmeseBody : 'N/A'),
          awayOdds: previous[eventKey]?.awayOdds ?? valueToInput(event.awayTeamId === event.overTeamId ? event.burmeseBody : 'N/A'),
          goalOdds: previous[eventKey]?.goalOdds ?? valueToInput(event.burmeseGoal ?? event.goalLine),
          overTeamId: previous[eventKey]?.overTeamId ?? event.overTeamId ?? null,
          underTeamId: previous[eventKey]?.underTeamId ?? event.underTeamId ?? null,
        }
      })
      return next
    })
  }, [allEvents])

  const selectedRows = useMemo(
    () =>
      allEvents
        .map((event) => {
          const eventKey = String(event.id)
          const selection = selectionMap[eventKey] ?? { single: false, parlay: false }
          return {
            ...event,
            ...selection,
            overTeamId: draftValues[eventKey]?.overTeamId ?? event.overTeamId ?? null,
            underTeamId: draftValues[eventKey]?.underTeamId ?? event.underTeamId ?? null,
            emergencyOdds: draftValues[eventKey] ?? {
              homeOdds: '',
              awayOdds: '',
              goalOdds: '',
              overTeamId: event.overTeamId ?? null,
              underTeamId: event.underTeamId ?? null,
            },
          }
        })
        .filter((event) => event.single || event.parlay),
    [allEvents, draftValues, selectionMap],
  )

  const getEmergencyRow = (event) => {
    const eventKey = String(event.id)
    const selection = selectionMap[eventKey] ?? { single: false, parlay: false }
    return {
      ...event,
      ...selection,
      overTeamId: draftValues[eventKey]?.overTeamId ?? event.overTeamId ?? null,
      underTeamId: draftValues[eventKey]?.underTeamId ?? event.underTeamId ?? null,
      emergencyOdds: draftValues[eventKey] ?? {
        homeOdds: '',
        awayOdds: '',
        goalOdds: '',
        overTeamId: event.overTeamId ?? null,
        underTeamId: event.underTeamId ?? null,
      },
    }
  }

  const handleDraftChange = (event, field, value) => {
    const eventKey = String(event.id)
    setDraftValues((previous) => {
      const current = {
        homeOdds: '',
        awayOdds: '',
        goalOdds: '',
        overTeamId: event.overTeamId ?? null,
        underTeamId: event.underTeamId ?? null,
        ...(previous[eventKey] ?? {}),
      }
      const next = {
        ...current,
        [field]: value,
      }
      const homeHasValue = field === 'homeOdds' ? hasOddsValue(value) : hasOddsValue(next.homeOdds)
      const awayHasValue = field === 'awayOdds' ? hasOddsValue(value) : hasOddsValue(next.awayOdds)

      if (field === 'homeOdds' && homeHasValue) {
        next.overTeamId = event.homeTeamId ?? null
        next.underTeamId = event.awayTeamId ?? null
      } else if (field === 'awayOdds' && awayHasValue) {
        next.overTeamId = event.awayTeamId ?? null
        next.underTeamId = event.homeTeamId ?? null
      } else if (!homeHasValue && !awayHasValue) {
        next.overTeamId = event.overTeamId ?? null
        next.underTeamId = event.underTeamId ?? null
      }

      return {
        ...previous,
        [eventKey]: next,
      }
    })
  }

  const handleSave = () => {
    onSave?.(selectedRows)
  }

  if (!events.length) {
    return (
      <div className="overflow-hidden rounded-3xl border border-customOrange/20 bg-white/95 shadow-xl shadow-customOrange/5 dark:border-customSurfaceMuted dark:bg-customSurface">
        <div className="flex min-h-[320px] items-center justify-center p-6 text-center">
          <CustomNoData
            title="Emergency buffer is clear"
            description="No buffered events match your filters yet."
          />
        </div>
        {pagination ? (
          <PaginationV2
            startIndex={pagination.startIndex}
            endIndex={pagination.endIndex}
            totalItems={pagination.totalItems}
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={pagination.onPageChange}
            itemLabel="emergency events"
          />
        ) : null}
      </div>
    )
  }

  return (
    <section className="overflow-hidden rounded-3xl border border-customOrange/25 bg-white/95 shadow-xl shadow-customOrange/10 dark:border-customSurfaceMuted dark:bg-customSurface">
      <div className="flex flex-col gap-3 border-b border-customMuted/20 bg-white/80 px-4 py-3 dark:border-customSurfaceMuted dark:bg-custom_dark_secondary/40 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-lg font-bold text-customOrange dark:text-customHeading">
            Emergency V0
          </h2>
          <p className="text-xs font-medium text-customSecondaryText dark:text-customMuted">
            Manual odds control for selected buffer events.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !selectedRows.length}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-customAccentBlue px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-customAccentBlue/20 transition hover:bg-customAccentBlue/85 focus:outline-none focus:ring-2 focus:ring-customAccentBlue/30 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-customAccent dark:text-customHeading dark:shadow-customAccent/20"
          >
            {saving ? (
              <>
                <span className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Saving...
              </>
            ) : (
              <>
                <FaSave />
                Save Betting
              </>
            )}
          </button>
          {userDetails?.companyId === "909" ? (
            <button
              type="button"
              onClick={onSaveMultiple}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-customVibrantGreen/70 bg-white px-5 py-2.5 text-sm font-bold text-customInk shadow-sm transition hover:border-customVibrantGreen/40 hover:bg-customVibrantGreen/5 focus:outline-none focus:ring-2 focus:ring-customVibrantGreen/30 disabled:cursor-not-allowed disabled:border-customSurfaceMuted disabled:bg-customSlate/40 disabled:text-customMuted dark:border-customVibrantGreen/60 dark:bg-customSurface dark:text-customHeading dark:hover:bg-customSurfaceMuted/80"
            >
              <FaUpload size={15} className="text-customVibrantGreen dark:text-customHeading" />
              Save to multiple bettings
            </button>
          ) : null}
        </div>
      </div>

      <div className="hidden min-w-full overflow-x-auto lg:block px-2">
        <table className="min-w-full divide-y divide-customMuted/30 dark:divide-customSurfaceMuted">
          <thead className="bg-background text-xs uppercase tracking-wide text-customLightMilkWhite dark:bg-custom_dark_secondary/60 dark:text-customMuted">
            <tr>
              <th className="px-4 py-2 text-left font-semibold">Event</th>
              <th className="px-4 py-2 text-left font-semibold">Odds</th>
              <th className="px-4 py-2 text-left font-semibold">Goal odds</th>
              <th className="px-4 py-2 text-left font-semibold">Selection</th>
              <th className="px-4 py-2 text-left font-semibold">Status</th>
              <th className="px-4 py-2 text-left font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-customMuted/20 text-sm dark:divide-customSurfaceMuted/70">
            {events.map((event) => {
              const eventKey = String(event.id)
              const selection = selectionMap[eventKey] ?? { single: false, parlay: false }
              const values = draftValues[eventKey] ?? {
                homeOdds: '',
                awayOdds: '',
                goalOdds: '',
                overTeamId: event.overTeamId ?? null,
                underTeamId: event.underTeamId ?? null,
              }
              const currentOverTeamId = values.overTeamId ?? event.overTeamId

              return (
                <tr
                  key={event.id}
                  className={`${event.isDuplicate ? 'border-l-[3px] border-customAltas' : ''} transition hover:bg-customOrange/5 dark:hover:bg-customSurfaceMuted/40`}
                >
                  <td className="min-w-[260px] px-4 py-3 align-top">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2 text-sm font-semibold text-customInk dark:text-customHeading">
                        <span className={event.homeTeamId === currentOverTeamId ? 'text-customFontRed' : ''}>
                          {event.homeTeam}
                        </span>
                        <span className="text-[10px] font-bold uppercase tracking-wide text-customMuted">
                          vs
                        </span>
                        <span className={event.awayTeamId === currentOverTeamId ? 'text-customFontRed' : ''}>
                          {event.awayTeam}
                        </span>
                      </div>
                      <div className="text-xs text-customSecondaryText dark:text-customMuted">
                        {event.leagueName}
                        {event.leagueCountry ? ` | ${event.leagueCountry}` : ''}
                      </div>
                      <div className="text-xs text-customSecondaryText dark:text-customMuted">
                        {formatKickoff(event.kickoff)} |{' '}
                        <span className="text-customAccent dark:text-blue">{eventKey}</span> |{' '}
                        {event.isDuplicate ? (
                          <span className="text-customFontRed">Duplicate</span>
                        ) : (
                          <span className="text-customVibrantGreen">Unique</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <EmergencyOddsInputs
                      values={values}
                      onChange={(field, value) => handleDraftChange(event, field, value)}
                    />
                  </td>
                  <td className="px-4 py-3 align-top">
                    <EmergencyGoalInput
                      values={values}
                      onChange={(field, value) => handleDraftChange(event, field, value)}
                    />
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="grid gap-2">
                      <EventSelectionCheckbox
                        type="single"
                        tone="single"
                        label="Single"
                        checked={Boolean(selection.single)}
                        disabled={!event.singleEligible}
                        onChange={(checked) => onToggleSelection?.(event.id, 'single', checked)}
                      />
                      <EventSelectionCheckbox
                        type="parlay"
                        tone="parlay"
                        label="Parlay"
                        checked={Boolean(selection.parlay)}
                        disabled={!event.parlayEligible}
                        onChange={(checked) => onToggleSelection?.(event.id, 'parlay', checked)}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <span className="inline-flex items-center gap-2 rounded-full bg-customOrange/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-customOrange">
                      Emergency
                    </span>
                  </td>
                  <td className="px-4 py-3 align-center">
                   {rowSaveLoading[eventKey] ? (
                     <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-customAccentBlue border-t-transparent" />
                   ) : (
                     <FaSave
                       className="cursor-pointer text-customAccentBlue dark:text-customAccent"
                       size="25"
                       onClick={() => onManualSave?.(getEmergencyRow(event))}
                     />
                   )}
                   {/* Future action buttons can be added here */}         
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="grid gap-3 p-3 lg:hidden">
        {events.map((event) => {
          const eventKey = String(event.id)
          const selection = selectionMap[eventKey] ?? { single: false, parlay: false }
          const values = draftValues[eventKey] ?? {
            homeOdds: '',
            awayOdds: '',
            goalOdds: '',
            overTeamId: event.overTeamId ?? null,
            underTeamId: event.underTeamId ?? null,
          }
          const currentOverTeamId = values.overTeamId ?? event.overTeamId

          return (
            <article
              key={event.id}
              className="rounded-2xl border border-customOrange/20 bg-white p-4 shadow-md shadow-customOrange/10 dark:border-customSurfaceMuted dark:bg-customSurface"
            >
              <header className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold text-customInk dark:text-customHeading">
                    <span className={event.homeTeamId === currentOverTeamId ? 'text-customFontRed' : ''}>
                      {event.homeTeam}
                    </span>{' '}
                    vs{' '}
                    <span className={event.awayTeamId === currentOverTeamId ? 'text-customFontRed' : ''}>
                      {event.awayTeam}
                    </span>
                  </h3>
                  <p className="text-xs text-customSecondaryText dark:text-customMuted">
                    {event.leagueName}
                    {event.leagueCountry ? ` | ${event.leagueCountry}` : ''}
                  </p>
                </div>
                <span className="rounded-full bg-customOrange/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-customOrange">
                  Emergency
                </span>
              </header>

              <div className="mt-4 grid gap-3 text-xs text-customSecondaryText dark:text-customMuted">
                <div>
                  <div className="font-semibold uppercase tracking-wide text-customMuted">Kick-off</div>
                  <div className="text-sm text-customInk dark:text-customHeading">
                    {formatKickoff(event.kickoff)}
                  </div>
                </div>
                <EmergencyOddsInputs
                  values={values}
                  onChange={(field, value) => handleDraftChange(event, field, value)}
                />
                <EmergencyGoalInput
                  values={values}
                  onChange={(field, value) => handleDraftChange(event, field, value)}
                />
              </div>

              <div className="mt-4 grid gap-2">
                <EventSelectionCheckbox
                  type="single"
                  tone="single"
                  label="Single"
                  checked={Boolean(selection.single)}
                  disabled={!event.singleEligible}
                  onChange={(checked) => onToggleSelection?.(event.id, 'single', checked)}
                />
                <EventSelectionCheckbox
                  type="parlay"
                  tone="parlay"
                  label="Parlay"
                  checked={Boolean(selection.parlay)}
                  disabled={!event.parlayEligible}
                  onChange={(checked) => onToggleSelection?.(event.id, 'parlay', checked)}
                />
              </div>
            </article>
          )
        })}
      </div>

      {pagination ? (
        <PaginationV2
          startIndex={pagination.startIndex}
          endIndex={pagination.endIndex}
          totalItems={pagination.totalItems}
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={pagination.onPageChange}
          itemLabel="emergency events"
        />
      ) : null}
    </section>
  )
}

export default EmergencyV0
